import { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { FeedbackAction } from '@/lib/types';

interface FeedbackButtonsProps {
  onSubmit: (action: FeedbackAction, feedbackText?: string) => void;
  disabled?: boolean;
  isSubmitting?: boolean;
}

export default function FeedbackButtons({
  onSubmit,
  disabled = false,
  isSubmitting = false,
}: FeedbackButtonsProps) {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedAction, setSelectedAction] = useState<FeedbackAction | null>(null);

  const handleThumbsUp = () => {
    setSelectedAction('sent_as_is');
    onSubmit('sent_as_is');
  };

  const handleThumbsDown = () => {
    setSelectedAction('rejected');
    setShowFeedbackInput(true);
  };

  const handleDetailedFeedback = () => {
    setSelectedAction('rejected');
    setShowFeedbackInput(true);
  };

  const handleSubmitFeedback = () => {
    if (selectedAction) {
      onSubmit(selectedAction, feedbackText || undefined);
      setShowFeedbackInput(false);
      setFeedbackText('');
    }
  };

  const handleCancelFeedback = () => {
    setShowFeedbackInput(false);
    setFeedbackText('');
    setSelectedAction(null);
  };

  const isDisabled = disabled || isSubmitting;

  if (showFeedbackInput) {
    return (
      <div className="space-y-2 p-3 rounded-lg border bg-background">
        <label className="block text-xs font-medium">
          Optional: Tell us what went wrong
        </label>
        <textarea
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder="E.g., tone was too formal, missing key information, factually incorrect..."
          className="w-full min-h-[80px] px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
          disabled={isSubmitting}
        />
        <div className="flex gap-2">
          <button
            onClick={handleCancelFeedback}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-medium border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitFeedback}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">How was this draft?</span>
      <div className="flex gap-1">
        <button
          onClick={handleThumbsUp}
          disabled={isDisabled}
          className={cn(
            'p-2 rounded-lg border transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            selectedAction === 'sent_as_is' && 'bg-green-50 border-green-500 text-green-600'
          )}
          title="Sent as-is (Cmd+Shift+U)"
          aria-label="Thumbs up - sent as-is"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          onClick={handleThumbsDown}
          disabled={isDisabled}
          className={cn(
            'p-2 rounded-lg border transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            selectedAction === 'rejected' && 'bg-red-50 border-red-500 text-red-600'
          )}
          title="Rejected or needs improvement"
          aria-label="Thumbs down - rejected"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
        <button
          onClick={handleDetailedFeedback}
          disabled={isDisabled}
          className="p-2 rounded-lg border transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          title="Provide detailed feedback"
          aria-label="Add detailed feedback"
        >
          <MessageSquare className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
